import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, FormattedNumber } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import IconCoins from "app/pages/.shared/static/icons/IconCoins";
import IconExternalLink from "app/pages/.shared/IconExternalLink";
import InfoPopover from "app/components/ui/InfoPopover/InfoPopover";
import { getCurrencySymbolFromCode } from "app/utils/currencies";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import "./MilesEarned.scss";

const MilesEarned = ({
	price,
	displayMiles,
	loyalty,
	milesPerCurrency,
	currency,
	prefixLabel,
	prefixMessageId,
	suffixLabel,
	moreDetailsLink,
}) => {
	const { track } = useAnalytics();
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	if (!displayMiles || !price) {
		return null;
	}

	const rate = parseFloat(milesPerCurrency) || 1;
	const milesCount = Math.floor(price * rate);
	const currencySymbol = getCurrencySymbolFromCode(currency) || currency;

	// If any custom label is passed, only show what's explicitly provided
	const hasCustomLabels =
		prefixLabel !== undefined || prefixMessageId !== undefined || suffixLabel !== undefined;

	const getPrefix = () => {
		if (!hasCustomLabels) {
			return <FormattedMessage id="miles.earned.title" />;
		}
		if (prefixMessageId) {
			return <FormattedMessage id={prefixMessageId} values={{ loyalty }} />;
		}
		return prefixLabel;
	};

	const prefix = getPrefix();

	const suffix = hasCustomLabels ? suffixLabel : <FormattedMessage id="miles.earned.suffix" />;

	const handleClick = e => {
		e.stopPropagation();
		e.preventDefault();
	};

	const handleInfoOpen = isOpen => {
		if (isOpen) {
			track("miles_info_click", { device: isMobile ? "mobile" : "desktop" });
		}
	};

	const popoverContent = (
		<div className="miles-earned-popover">
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				isBold
				className="miles-earned-popover__title"
			>
				<FormattedMessage id="miles.earned.popover.calculation.title" />
			</Typography>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				className="miles-earned-popover__text"
			>
				<FormattedMessage
					id="miles.earned.popover.calculation.text"
					values={{ currencySymbol, rate, loyalty }}
				/>
			</Typography>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				isBold
				className="miles-earned-popover__title"
			>
				<FormattedMessage id="miles.earned.popover.distribution.title" />
			</Typography>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				className="miles-earned-popover__text"
			>
				<FormattedMessage
					id="miles.earned.popover.distribution.text"
					values={{ loyalty }}
				/>
			</Typography>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				isBold
				className="miles-earned-popover__title"
			>
				<FormattedMessage id="miles.earned.popover.timing.title" />
			</Typography>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				className="miles-earned-popover__text"
			>
				<FormattedMessage id="miles.earned.popover.timing.text" />
			</Typography>
			{moreDetailsLink && (
				<Button
					variant={BUTTON_VARIANTS.BORDERLESS}
					icon={<IconExternalLink />}
					alignIcon="right"
					className="miles-earned-popover__link"
					to={moreDetailsLink}
					target="_blank"
					rel="noopener noreferrer"
				>
					<FormattedMessage id="miles.earned.popover.link" />
				</Button>
			)}
		</div>
	);

	return (
		<div className="miles-earned" onClick={handleClick}>
			<IconCoins className="miles-earned__icon" />
			<div className="miles-earned__content">
				{prefix && (
					<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} component="span">
						{prefix}{" "}
					</Typography>
				)}
				<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} isBold component="span">
					<FormattedNumber value={milesCount} />+
				</Typography>
				{suffix && (
					<Typography variant={TYPOGRAPHY_VARIANTS.SMALL} component="span">
						{" "}
						{suffix}
					</Typography>
				)}
				<InfoPopover iconClassName="miles-earned__info-icon" onOpenChange={handleInfoOpen}>
					{popoverContent}
				</InfoPopover>
			</div>
		</div>
	);
};

MilesEarned.propTypes = {
	price: PropTypes.number,
	displayMiles: PropTypes.bool,
	loyalty: PropTypes.string,
	milesPerCurrency: PropTypes.string,
	currency: PropTypes.string,
	prefixLabel: PropTypes.node,
	prefixMessageId: PropTypes.string,
	suffixLabel: PropTypes.node,
	moreDetailsLink: PropTypes.string,
};

export default memo(MilesEarned);
